/* ============================================================================
   Twilight — Globo Card Enhancement
   ----------------------------------------------------------------------------
   Globo Smart Filter renders product cards from its own template, bypassing
   Dawn's card-product.liquid. Globo's rendered DOM has no status badge slot
   and no provenance metadata — both are required by the Twilight design
   system (prototype-collection.html).

   This script:
     1. Finds every .spf-product-card on the page that hasn't been enhanced.
     2. Extracts the product handle from the image-wrapper href.
     3. Fetches /products/HANDLE.js (Shopify's product JSON endpoint).
     4. Injects a .twilight-status pill into the image wrapper (top-left).
     5. Injects a .twilight-provenance pill row below the price.
     6. Watches the grid for re-renders (Globo replaces the DOM on filter
        change) via MutationObserver, re-running enhancement.

   Status pill rules (in order of precedence):
     !product.available                          → SOLD OUT (gray, no dot)
     compare_at_price > price                    → SALE (ember)
     created < 90 days ago                       → NEW (blue)
     otherwise                                   → IN STOCK (green)

   Provenance pill rules (from product.tags):
     "provenance:field"     → +FIELD pill (ember)
     "provenance:curator"   → +CURATOR pill (blue)
     "provenance:customer"  → +CUSTOMER pill (green)
     "provenance:supplier"  → +SUPPLIER pill (muted)

   CSS for these pills lives in assets/custom.css under the
   "Authoritative Globo Card" section (.twilight-status, .twilight-provenance).
   ============================================================================ */

(function () {
  'use strict';

  const PRODUCT_CACHE = new Map();
  const NEW_PRODUCT_AGE_DAYS = 60; // keep in sync with the Shopify Flow 'new-arrival' tag window
  const ENHANCED_ATTR = 'data-twilight-enhanced';

  function svgIcon(kind) {
    const stroke = 'currentColor';
    switch (kind) {
      case 'field':
        return `<svg viewBox="0 0 12 12" fill="none" stroke="${stroke}" stroke-width="2"><path d="M6 1v10M1 6h10"/></svg>`;
      case 'curator':
        return `<svg viewBox="0 0 12 12" fill="none" stroke="${stroke}" stroke-width="2"><polygon points="6 1 7.5 4.5 11 5 8.5 7.5 9 11 6 9 3 11 3.5 7.5 1 5 4.5 4.5"/></svg>`;
      case 'customer':
        return `<svg viewBox="0 0 12 12" fill="none" stroke="${stroke}" stroke-width="2"><path d="M3 7l3 3 4-7"/></svg>`;
      case 'supplier':
        return `<svg viewBox="0 0 12 12" fill="none" stroke="${stroke}" stroke-width="2"><circle cx="6" cy="6" r="5"/><path d="M3 6l2 2 4-4"/></svg>`;
      default:
        return '';
    }
  }

  function classifyStatus(product) {
    if (!product.available) {
      return { kind: 'sold-out', label: 'Sold out' };
    }
    if (product.compare_at_price && product.compare_at_price > product.price) {
      return { kind: 'sale', label: 'Sale' };
    }
    const createdAt = product.created_at ? new Date(product.created_at) : null;
    if (createdAt) {
      const ageDays = (Date.now() - createdAt.getTime()) / 86400000;
      if (ageDays >= 0 && ageDays < NEW_PRODUCT_AGE_DAYS) {
        return { kind: 'new', label: 'New' };
      }
    }
    return { kind: 'in-stock', label: 'In stock' };
  }

  function buildStatusEl(status) {
    const el = document.createElement('span');
    el.className = `twilight-status twilight-status--${status.kind}`;
    el.textContent = status.label;
    return el;
  }

  function buildProvenancePills(tags) {
    if (!Array.isArray(tags) || tags.length === 0) return null;
    const levels = ['field', 'curator', 'customer', 'supplier']
      .filter(level => tags.some(t => t.toLowerCase() === `provenance:${level}`));
    if (levels.length === 0) return null;

    const row = document.createElement('div');
    row.className = 'twilight-provenance';
    levels.forEach(level => {
      const pill = document.createElement('span');
      pill.className = `twilight-provenance__pill twilight-provenance__pill--${level}`;
      pill.innerHTML = `${svgIcon(level)}${level.charAt(0).toUpperCase() + level.slice(1)}`;
      row.appendChild(pill);
    });
    return row;
  }

  async function fetchProduct(handle) {
    if (PRODUCT_CACHE.has(handle)) return PRODUCT_CACHE.get(handle);
    const promise = fetch(`/products/${handle}.js`, { credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : null)
      .catch(() => null);
    PRODUCT_CACHE.set(handle, promise);
    return promise;
  }

  async function enhanceCard(card) {
    if (card.hasAttribute(ENHANCED_ATTR)) return;
    card.setAttribute(ENHANCED_ATTR, 'pending');

    // Cards must not link anywhere except the product page. Globo wraps the
    // vendor in an <a href="/collections/vendors?q=..."> filter link — unwrap
    // it to a plain span (the sidebar Brand filter is the filtering surface).
    // Runs before the early returns below so it applies to every card.
    card.querySelectorAll('.spf-product-card__vendor a').forEach((a) => {
      const span = document.createElement('span');
      span.textContent = a.textContent;
      a.replaceWith(span);
    });

    const linkEl = card.querySelector('.spf-product-card__image-wrapper');
    const href = linkEl?.getAttribute('href') || '';
    const match = href.match(/\/products\/([^/?#]+)/);
    if (!match) { card.setAttribute(ENHANCED_ATTR, 'no-handle'); return; }

    const handle = match[1];
    const product = await fetchProduct(handle);
    if (!product) { card.setAttribute(ENHANCED_ATTR, 'fetch-failed'); return; }

    if (linkEl && !linkEl.querySelector('.twilight-status')) {
      const status = classifyStatus(product);
      const statusEl = buildStatusEl(status);
      linkEl.appendChild(statusEl);
    }

    const priceWrapper = card.querySelector('.spf-product-card__price-wrapper');
    if (priceWrapper && !card.querySelector('.twilight-provenance')) {
      const provRow = buildProvenancePills(product.tags);
      if (provRow) priceWrapper.parentNode.insertBefore(provRow, priceWrapper.nextSibling);
    }

    card.setAttribute(ENHANCED_ATTR, 'done');
  }

  function enhanceAllCards() {
    const cards = document.querySelectorAll('.spf-product-card:not([data-twilight-enhanced="done"])');
    cards.forEach(card => { enhanceCard(card).catch(() => {}); });
  }

  function watchForRerenders() {
    const target = document.querySelector('[data-globo-filter-items], #gf-products, .gf-products');
    if (!target) return;
    const observer = new MutationObserver(() => {
      enhanceAllCards();
    });
    observer.observe(target, { childList: true, subtree: true });
  }

  function init() {
    enhanceAllCards();
    watchForRerenders();
    cleanupQikifyTitles();
    enhancePDP();
    enhanceCart();
  }

  // Inject the design-system trust strip below the cart order summary.
  function enhanceCart() {
    const footer = document.querySelector('.cart__footer');
    if (!footer) return;
    const wrapper = footer.closest('.cart__footer-wrapper');
    if (!wrapper) return;
    if (wrapper.querySelector('.twilight-cart-trust')) return;
    const trust = document.createElement('div');
    trust.className = 'twilight-cart-trust';
    trust.innerHTML = `
      <div class="twilight-cart-trust__item">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2"><polyline points="14 4 6 12 2 8"/></svg>
        Free shipping over $99
      </div>
      <div class="twilight-cart-trust__item">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2"><polyline points="14 4 6 12 2 8"/></svg>
        30-day field guarantee
      </div>
      <div class="twilight-cart-trust__item">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2"><polyline points="14 4 6 12 2 8"/></svg>
        Secure checkout · Shop Pay, Apple Pay, PayPal
      </div>
    `;
    wrapper.appendChild(trust);
  }

  // PDP enhancements: comprehensive port of prototype-pdp.html .pdp-info
  // structure onto Dawn's main-product DOM.
  // Order target: vendor → title → badges → summary → verified → buy → meta
  async function enhancePDP() {
    const infoContainer = document.querySelector('.product__info-container');
    if (!infoContainer) return;
    if (infoContainer.hasAttribute('data-twilight-pdp')) return;
    infoContainer.setAttribute('data-twilight-pdp', 'pending');

    let product = null;
    try {
      const handle = window.location.pathname.match(/\/products\/([^\/?#]+)/)?.[1];
      if (handle) product = await fetchProduct(handle);
    } catch (e) { /* fall through */ }

    // Build the buy card first (needed by other injectors)
    wrapBuyCard(infoContainer);

    // 1. Vendor above title
    if (product) injectVendor(infoContainer, product);

    // 2. Badges row right after title (In Stock pill + category tags)
    if (product) injectBadges(infoContainer, product);

    // 3. Move description BEFORE the buy card (prototype order)
    reorderDescription(infoContainer);

    // 4. Verified-by row above buy card (from tags / reviews)
    if (product) injectVerifiedRow(infoContainer, product);

    // 5. Inside buy card: ship info, price-row layout, secondary actions
    if (product) enhanceBuyCard(infoContainer, product);

    // 6. Spec sheet below buy card
    if (product) injectSpecSheet(infoContainer, product);

    infoContainer.setAttribute('data-twilight-pdp', 'done');
  }

  function injectVendor(container, product) {
    if (container.querySelector('.twilight-pdp-vendor')) return;
    if (!product.vendor) return;
    const el = document.createElement('p');
    el.className = 'twilight-pdp-vendor';
    el.textContent = product.vendor;
    const title = container.querySelector('.product__title');
    if (title) container.insertBefore(el, title);
  }

  function injectBadges(container, product) {
    if (container.querySelector('.twilight-pdp-badges')) return;
    const row = document.createElement('div');
    row.className = 'twilight-pdp-badges';

    // Status pill
    let statusKind = 'in-stock', statusLabel = 'In stock';
    if (!product.available) { statusKind = 'sold-out'; statusLabel = 'Sold out'; }
    else if (product.compare_at_price && product.compare_at_price > product.price) {
      statusKind = 'sale'; statusLabel = 'Sale';
    }
    const status = document.createElement('span');
    // .twilight-status--inline is a non-absolutely-positioned variant for in-flow
    // use in the PDP badges row (the base .twilight-status is absolute-positioned
    // for grid card image overlays).
    status.className = `twilight-status twilight-status--inline twilight-status--${statusKind}`;
    status.textContent = statusLabel;
    row.appendChild(status);

    // Category badges from product.type or product.tags.
    // Filter rules:
    //   - Skip duplicates of product.vendor (vendor shown separately above title)
    //   - Skip Shopify system tags (Shopify Collective, etc.)
    //   - Skip our internal taxonomy prefixes (provenance:, spf:, etc.)
    //   - Skip "Sale" / "New" tags (handled by status pill)
    const vendor = (product.vendor || '').toLowerCase();
    const skipExact = new Set(['shopify collective']);
    const skipPrefix = /^(provenance|spf|sale|new|sold-out|collection):/i;
    const isBadValue = (v) => {
      if (!v) return true;
      const vl = v.toLowerCase();
      if (vl === vendor) return true;
      if (skipExact.has(vl)) return true;
      if (skipPrefix.test(v)) return true;
      if (/^(sale|new)$/i.test(v)) return true;
      return false;
    };

    const categories = [];
    if (product.type && !isBadValue(product.type)) categories.push(product.type);
    (product.tags || []).forEach(t => {
      if (categories.length >= 3) return;
      if (isBadValue(t)) return;
      if (categories.includes(t)) return;
      categories.push(t);
    });
    categories.forEach(cat => {
      const a = document.createElement('a');
      a.className = 'twilight-category-badge';
      a.href = `/collections/all?q=${encodeURIComponent(cat)}`;
      a.textContent = cat;
      row.appendChild(a);
    });

    const title = container.querySelector('.product__title');
    if (title && title.nextSibling) container.insertBefore(row, title.nextSibling);
    else container.appendChild(row);
  }

  function reorderDescription(container) {
    const desc = container.querySelector('.product__description');
    const buyCard = container.querySelector('.twilight-buy-card');
    const verified = container.querySelector('.twilight-verified');
    if (!desc || !buyCard) return;
    // Insert description before verified (if exists) or buy card
    const target = verified || buyCard;
    if (desc.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_PRECEDING) {
      // desc is already after target — that means desc comes after; we want desc before target
      container.insertBefore(desc, target);
    } else {
      // If somehow desc is already before, leave alone
    }
  }

  function enhanceBuyCard(container, product) {
    const card = container.querySelector('.twilight-buy-card');
    if (!card) return;

    // 1. Wrap price into a price-row. Ship info only renders if it comes
    // from metafields — no hardcoded "2 days" / "Free shipping" marketing
    // copy. Fields read:
    //   custom.ships_in           (e.g., "2 days", "1–3 weeks")
    //   custom.free_shipping      (truthy → green "Free shipping" line)
    if (!card.querySelector('.twilight-price-row')) {
      const priceEl = card.querySelector('.price');
      if (priceEl) {
        const row = document.createElement('div');
        row.className = 'twilight-price-row';
        priceEl.parentNode.insertBefore(row, priceEl);
        row.appendChild(priceEl);

        // /products/HANDLE.js doesn't include metafields by default, so we
        // read them from inline data on the page if present. This is a
        // best-effort — for now, the ship element only renders when the
        // theme owner exposes the data (separate liquid snippet to ship
        // later if needed).
        const shipsIn = readInlineProductMeta('ships_in');
        const freeShipping = readInlineProductMeta('free_shipping');
        if (shipsIn || freeShipping) {
          const ship = document.createElement('div');
          ship.className = 'twilight-ship';
          const lines = [];
          if (shipsIn) lines.push(`Ships in ${escapeHtml(shipsIn)}`);
          if (freeShipping) lines.push(`<strong>Free shipping</strong>`);
          ship.innerHTML = lines.join('<br>');
          row.appendChild(ship);
        }
      }
    }

    // 2. Append secondary actions row at the END of the card
    if (!card.querySelector('.twilight-secondary-actions')) {
      const row = document.createElement('div');
      row.className = 'twilight-secondary-actions';
      row.innerHTML = `
        <button type="button" class="twilight-secondary-btn" data-action="save" aria-label="Save to wishlist">Save</button>
        <button type="button" class="twilight-secondary-btn" data-action="compare" aria-label="Compare product">Compare</button>
        <button type="button" class="twilight-secondary-btn" data-action="share" aria-label="Share product">Share</button>
      `;
      card.appendChild(row);

      // Wire Share button to native share API (or copy link fallback)
      row.querySelector('[data-action="share"]')?.addEventListener('click', async () => {
        const url = window.location.href;
        if (navigator.share) {
          try { await navigator.share({ url, title: document.title }); } catch (e) { /* user cancelled */ }
        } else {
          try { await navigator.clipboard.writeText(url); } catch (e) { /* clipboard unavailable */ }
        }
      });
      // Save / Compare are placeholders — could wire to localStorage later
    }

    // 3. Update Add-to-cart label to include price ("Add to cart — $X.XX")
    updateAtcLabel(card, product);
  }

  function updateAtcLabel(card, product) {
    const submit = card.querySelector('.product-form__submit, button[name="add"], button.button--primary');
    if (!submit) return;
    const span = submit.querySelector('span:first-of-type') || submit;
    const formatPrice = (cents) => {
      const dollars = (cents / 100).toFixed(2);
      return `$${dollars}`;
    };
    const price = product.price;
    if (!price) return;
    const original = (span.textContent || '').trim();
    // Only update if it's the default "Add to cart" (not already showing price)
    if (/^(Add to cart|Sold out)/i.test(original) && !/\$/.test(original)) {
      span.textContent = `${original.replace(/\s*[—\-].*/, '')} — ${formatPrice(price)}`;
    }
  }

  // Group price + bread-pay + quantity + buy buttons into a single
  // .twilight-buy-card div, so CSS can frame them as one card. The blocks
  // are NOT consecutive in Dawn — description sits between price and
  // quantity. We collect by selector and rearrange in render order.
  function wrapBuyCard(container) {
    if (container.querySelector('.twilight-buy-card')) return;
    const price = container.querySelector('.price');
    if (!price) return;

    // Resolve each piece to its direct child of .product__info-container.
    const toTop = (el) => {
      let n = el;
      while (n && n.parentElement && n.parentElement !== container) n = n.parentElement;
      return (n && n.parentElement === container) ? n : null;
    };

    const priceTop = toTop(price);
    const breadTop = toTop(container.querySelector('[class*="bread-pay"], .shopify-app-block:not(:first-of-type)'));
    const qtyTop = toTop(container.querySelector('.product-form__input--quantity, .product-form__quantity'));
    // Buy buttons live in a div that contains "Add to cart" submit + Shop Pay.
    let buyTop = null;
    container.querySelectorAll(':scope > div').forEach(d => {
      if (!buyTop && /Add to cart|product-form__submit/.test(d.outerHTML)) buyTop = d;
    });

    // Order: price, [bread-pay], quantity, buy
    const ordered = [priceTop, breadTop, qtyTop, buyTop].filter(Boolean);
    // Dedupe (in case bread-pay block IS the price wrapper)
    const seen = new Set();
    const uniqueOrdered = ordered.filter(n => {
      if (seen.has(n)) return false;
      seen.add(n);
      return true;
    });
    if (uniqueOrdered.length === 0) return;

    const card = document.createElement('div');
    card.className = 'twilight-buy-card';
    // Insert card where the price USED to be
    container.insertBefore(card, uniqueOrdered[0]);
    uniqueOrdered.forEach(b => card.appendChild(b));
  }

  function svgIconLg(kind) {
    const stroke = 'currentColor';
    switch (kind) {
      case 'field':    return `<svg viewBox="0 0 12 12" fill="none" stroke="${stroke}" stroke-width="2"><path d="M6 1v10M1 6h10"/></svg>`;
      case 'curator':  return `<svg viewBox="0 0 12 12" fill="none" stroke="${stroke}" stroke-width="2"><polygon points="6 1 7.5 4.5 11 5 8.5 7.5 9 11 6 9 3 11 3.5 7.5 1 5 4.5 4.5"/></svg>`;
      case 'customer': return `<svg viewBox="0 0 12 12" fill="none" stroke="${stroke}" stroke-width="2"><path d="M3 7l3 3 4-7"/></svg>`;
      case 'reviews':  return `<svg viewBox="0 0 12 12" fill="none" stroke="${stroke}" stroke-width="2"><path d="M3 7l3 3 4-7"/></svg>`;
      default: return '';
    }
  }

  // Inject a "Verified by" pill row above the .twilight-buy-card.
  function injectVerifiedRow(container, product) {
    if (container.querySelector('.twilight-verified')) return;
    const card = container.querySelector('.twilight-buy-card');
    if (!card) return;

    const tags = Array.isArray(product.tags) ? product.tags.map(t => t.toLowerCase()) : [];
    const has = (lvl) => tags.some(t => t === `provenance:${lvl}`);
    const pills = [];

    if (has('field')) pills.push({ kind: 'field', label: 'Field-Tested' });
    if (has('curator')) pills.push({ kind: 'curator', label: 'Curator-Approved' });
    if (has('customer')) pills.push({ kind: 'customer', label: 'Customer-Verified' });

    // Reviews count from AirReviews block if present
    const ratingEl = document.querySelector('.AirReviews-Blocks--RateCount, [class*="rating"]');
    const reviewLabel = document.querySelector('.AirReviews-Text--bodySm, [class*="review-count"]');
    if (ratingEl) {
      const rating = ratingEl.textContent?.trim();
      const count = reviewLabel?.textContent?.trim().match(/\d+/)?.[0] || '0';
      if (count !== '0') {
        pills.push({ kind: 'reviews', label: `${count} reviews · ${rating}` });
      }
    }

    if (pills.length === 0) return;

    const row = document.createElement('div');
    row.className = 'twilight-verified';
    row.innerHTML = `<span class="twilight-verified__label">Verified by:</span>` +
      pills.map(p => `<span class="twilight-verified__pill twilight-verified__pill--${p.kind}">${svgIconLg(p.kind)}${p.label}</span>`).join('');

    card.parentElement.insertBefore(row, card);
  }

  // Build a spec sheet KV grid from variant data.
  // Includes: SKU, Weight (with unit), Vendor, Type, plus any custom dimension
  // metafields if they exist (custom.dimensions_collapsed, etc.) — these are
  // populated by Shopify in the product.js response when the metafield is set.
  function injectSpecSheet(container, product) {
    if (container.querySelector('.twilight-specs')) return;

    const rows = [];
    const v0 = product.variants?.[0] || {};
    if (v0.sku) rows.push({ k: 'SKU', v: v0.sku });
    if (v0.weight && v0.weight > 0) {
      const unit = v0.weight_unit || 'g';
      rows.push({ k: 'Weight', v: `${v0.weight} ${unit}` });
    }
    if (product.vendor) rows.push({ k: 'Vendor', v: product.vendor });
    if (product.type) rows.push({ k: 'Type', v: product.type });

    if (rows.length === 0) return;

    const grid = document.createElement('div');
    grid.className = 'twilight-specs';
    grid.innerHTML = rows.map(r =>
      `<div class="twilight-specs__row"><span class="twilight-specs__key">${r.k}</span><span class="twilight-specs__val">${escapeHtml(r.v)}</span></div>`
    ).join('');

    const card = container.querySelector('.twilight-buy-card');
    if (card) card.parentElement.insertBefore(grid, card.nextSibling);
    else container.appendChild(grid);
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  // Read a product metafield value from any inline <meta> / <script type="application/ld+json">
  // / <data> tag on the page. Returns null if not found. Lets the theme expose
  // metafields via a small Liquid snippet without changing this JS.
  // Convention: <meta name="twilight:custom.<key>" content="<value>">
  function readInlineProductMeta(key) {
    const tag = document.querySelector(`meta[name="twilight:custom.${key}"]`);
    return tag?.content?.trim() || null;
  }

  // Qikify sets title="..." on every menu link, which triggers a browser-
  // native tooltip on hover ("SHOP ONLINE", "SHOP TRAILER GEAR", etc.).
  // This is redundant since the link text already says the same thing and
  // the mega-panel provides full context. Strip the title attributes.
  function cleanupQikifyTitles() {
    document.querySelectorAll('#qikify-tmenu-megamenu-0 [title], #qikify-tmenu-mobilemenu-1 [title], .tmenu_submenu [title]')
      .forEach(el => el.removeAttribute('title'));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  setTimeout(enhanceAllCards, 500);
  setTimeout(enhanceAllCards, 1500);
  // Qikify renders its menu post-DOMContentLoaded sometimes — run cleanup
  // a couple of times to catch late-rendered links.
  setTimeout(cleanupQikifyTitles, 500);
  setTimeout(cleanupQikifyTitles, 1500);
})();
